import { type FC } from 'react'

import { Box, SearchField, Spinner, Table } from '@factorialco/deprecated-design-system'
import isEmpty from 'lodash/isEmpty'

import { observer } from 'mobx-react'

import { ExplotableList } from 'modules/core/components/GroupingExplotable'
import createComputed from 'modules/core/hooks/createComputed'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import i18n from 'modules/core/lib/i18n'
import { matchText } from 'modules/core/lib/stringUtils'
import { getFullDisplayName } from 'modules/core/lib/userDisplayName'
import users from 'modules/core/store/collections/users'

import { type ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import employeesCollection from 'modules/employees/store/collections/employees'
import type Employee from 'modules/employees/store/models/Employee'
import type { Role } from 'modules/job_catalog/hooks/useRole'
import { employeeJobLevel } from 'modules/job_catalog/store/collections/levels'
import { employeeJobRole } from 'modules/job_catalog/store/collections/roles'
import policyAuthorizations from 'modules/permissions/store/collections/policyAuthorizations'

import ExpandableGroup from './ExpandableGroup'
import Row from './Row'

const getEmployeeUser = (employee: Employee) => {
  const userId = employee.attributes.access_id
  const user = users.get(userId)
  if (!user) {
    throw Error(`Error getting users with id = ${userId}`)
  }
  return user
}

interface Props {
  role: Role
  contractVersions: ContractVersions
}

const useFilteredEmployees = createComputed((employees: Employee[], search: string) => {
  return employees.filter((employee) => {
    const user = getEmployeeUser(employee)
    return (
      isEmpty(search) ||
      matchText(search, user.attributes.first_name?.trim() || '') ||
      matchText(
        search,
        getFullDisplayName({
          firstName: user.attributes.first_name,
          lastName: user.attributes.last_name,
          canSharePreferredName: employee.policy.can('sharePreferredName'),
          preferredName: employee.attributes.preferred_name,
        })
      )
    )
  })
})

const RoleEmployeesTable: FC<Props> = ({ role, contractVersions }) => {
  const { search, debouncedSearch, onSearch } = useDebouncedSearch()
  const levels =
    role.levels && role.levels.filter((x) => !x.isDefault).sort((a, b) => a.order - b.order)

  const employees = employeesCollection.toArray()

  const { loading: arePoliciesLoading } = useAsyncData(async () => {
    await policyAuthorizations.authorizePolicies('contracts.see_gross_salary', employees)
  }, [employees.length])

  const employeesToShow = useFilteredEmployees(employees, debouncedSearch).get()

  const employeesPerLevel = useUnsafeMemo(
    () => (levelId: string) =>
      employeesToShow.filter(
        (x) => employeeJobLevel(contractVersions, x)?.attributes.id === levelId
      ),
    [employeesToShow]
  )

  const roleEmployees = employeesToShow.filter(
    (x) => employeeJobRole(contractVersions, x)?.id === role.id
  )

  const showSalaryAmountColumn = roleEmployees.some((x) =>
    policyAuthorizations.for('contracts.see_gross_salary', x)
  )

  const rows = (employees: Employee[], level: { id: string; name: string }) =>
    employees.map((x) => (
      <Row
        level={level}
        key={x.attributes.id}
        employee={x}
        showSalaryAmountColumn={showSalaryAmountColumn}
      />
    ))

  const levelGroups =
    levels.length &&
    levels
      .sort((a, b) => a.order - b.order)
      .map((x) => {
        const employees = employeesPerLevel(x.id)

        return (
          <ExpandableGroup key={x.id} count={employees.length} level={x}>
            {rows(employees, x)}
          </ExpandableGroup>
        )
      })

  const defaultLevel = role.levels.find((x) => x.isDefault)
  const defaultLevelEmployees = defaultLevel && employeesPerLevel(defaultLevel.id)

  const rowForDefaultLevel =
    defaultLevel && defaultLevelEmployees?.length ? (
      rows(defaultLevelEmployees, defaultLevel)
    ) : (
      <Table.Blank colSpan={2} text={i18n.t('job_catalog.no_employees_assigned')} />
    )

  const options = () => (
    <Box width="full">
      <SearchField
        variant="big"
        search={search}
        onSearch={onSearch}
        placeholder={i18n.t('common.search')}
      />
    </Box>
  )

  if (arePoliciesLoading)
    return (
      <Box alignItems="center">
        <Spinner />
      </Box>
    )

  return (
    <Table overflowY="auto" noBottomRadius options={options} fullWidth>
      <Table noRadius hideBorder>
        <Table.Head>
          <Table.Row>
            <Table.HeadCell colSpan={4}>{i18n.t('employee_form.first_name')}</Table.HeadCell>
            <Table.HeadCell colSpan={4}>{i18n.t('employee_form.last_name')}</Table.HeadCell>
            <Table.HeadCell colSpan={4}>{i18n.t('employees.tenure')}</Table.HeadCell>
            <Table.HeadCell colSpan={4}>{i18n.t('common.manager')}</Table.HeadCell>
            {showSalaryAmountColumn && (
              <Table.HeadCell colSpan={4}>{i18n.t('job_catalog.salary_amount')}</Table.HeadCell>
            )}
            <Table.HeadCell colSpan={2} />
          </Table.Row>
        </Table.Head>
      </Table>
      <Table noRadius hideBorder>
        {levels.length ? (
          <ExplotableList>{levelGroups}</ExplotableList>
        ) : (
          <Table noRadius hideBorder>
            <Table.Body>{rowForDefaultLevel}</Table.Body>
          </Table>
        )}
      </Table>
    </Table>
  )
}

export default observer(RoleEmployeesTable)
